import { createContext, useContext, useEffect, useRef, useState, ReactNode } from "react";
import { supabase } from "@/integrations/supabase/client";
import type { User, Session } from "@supabase/supabase-js";
import { authRedirectUrl } from "@/lib/siteConfig";
import {
  trackSessionRestored,
  trackSessionRefreshed,
  trackSessionExpired,
  trackSessionSignedOut,
  trackSessionError,
} from "@/lib/appAnalytics";

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  emailVerified: boolean;
  signOut: () => Promise<void>;
  resendVerification: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  session: null,
  loading: true,
  emailVerified: false,
  signOut: async () => {},
  resendVerification: async () => {},
});

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  // Distinguishes a user-initiated sign-out from a session that expired.
  const explicitSignOutRef = useRef(false);

  useEffect(() => {
    const hydrateStartedAt = Date.now();

    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);

      if (event === "TOKEN_REFRESHED") {
        trackSessionRefreshed({ has_session: !!session });
        if (!session) trackSessionError("token_refresh", new Error("Token refresh returned no session"));
      }
      if (event === "SIGNED_OUT") {
        if (explicitSignOutRef.current) {
          explicitSignOutRef.current = false;
        } else {
          trackSessionExpired({ source: "auth_state_change" });
        }
      }

      // Send welcome email on first signup (not on subsequent logins)
      if (event === "SIGNED_IN" && session?.user) {
        const createdAt = new Date(session.user.created_at).getTime();
        const now = Date.now();
        // Only trigger if the account was created within the last 60 seconds
        if (now - createdAt < 60_000) {
          try {
            await supabase.functions.invoke("send-transactional-email", {
              body: {
                templateName: "welcome",
                recipientEmail: session.user.email,
                idempotencyKey: `welcome-${session.user.id}`,
                templateData: {
                  name: session.user.user_metadata?.first_name || session.user.user_metadata?.full_name?.split(" ")[0] || undefined,
                },
              },
            });
          } catch (err) {
            console.error("Failed to send welcome email:", err);
          }
        }
      }
    });

    supabase.auth.getSession()
      .then(({ data: { session }, error }) => {
        setSession(session);
        setUser(session?.user ?? null);
        setLoading(false);
        if (error) {
          trackSessionError("hydrate", error, { duration_ms: Date.now() - hydrateStartedAt });
        } else if (session) {
          trackSessionRestored(Date.now() - hydrateStartedAt);
        }
      })
      .catch((err) => {
        setLoading(false);
        trackSessionError("hydrate", err, { duration_ms: Date.now() - hydrateStartedAt });
      });

    return () => subscription.unsubscribe();
  }, []);

  const emailVerified = !!user?.email_confirmed_at || !!user?.phone;

  const signOut = async () => {
    explicitSignOutRef.current = true;
    const { error } = await supabase.auth.signOut();
    if (error) {
      explicitSignOutRef.current = false;
      trackSessionError("sign_out", error);
      return;
    }
    // Never leave one person's cached dashboard on a shared device.
    const { clearAllDashboardCaches } = await import("@/lib/dashboardCache");
    clearAllDashboardCaches();
    trackSessionSignedOut({ source: "user_action" });
  };

  const resendVerification = async () => {
    if (!user?.email) throw new Error("No email on file for this account.");
    const { error } = await supabase.auth.resend({
      type: "signup",
      email: user.email,
      options: { emailRedirectTo: authRedirectUrl("/dashboard") },
    });
    if (error) throw error;
  };

  return (
    <AuthContext.Provider value={{ user, session, loading, emailVerified, signOut, resendVerification }}>
      {children}
    </AuthContext.Provider>
  );
};
